/**
 * Permission checking utilities
 *
 * Checks if the current user has specific permissions based on their roles.
 * This is a client-side check for UI purposes only - the backend still enforces
 * all permission checks for security.
 */

// Role-based permission mapping
// This should match the backend permission assignments
const ROLE_PERMISSIONS = {
  Admin: [
    'view_users',
    'invite_user',
    'manage_users',
    'manage_user_roles',
    'edit_user',
    'deactivate_user',
    'view_cases',
    'create_case',
    'edit_case',
    'delete_case',
    'view_case',
    'view_clients',
    'create_client',
    'edit_client',
    'delete_client',
    'view_tasks',
    'create_task',
    'edit_task',
    'delete_task',
    'view_task',
    'view_projects',
    'create_project',
    'edit_project',
    'delete_project',
    'view_documents',
    'create_document',
    'edit_document',
    'delete_document',
    'view_document',
    'view_invoices',
    'create_invoice',
    'edit_invoice',
    'delete_invoice',
    'approve_invoice',
    'view_calendar',
    'create_calendar_event',
    'edit_calendar_event',
    'delete_calendar_event',
    'view_communications',
    'create_communication',
    'view_dashboard',
    'view_reports'
  ],
  Attorney: [
    'view_users',
    'invite_user',
    'edit_user',
    'view_cases',
    'create_case',
    'edit_case',
    'delete_case',
    'view_case',
    'assign_case',
    'view_clients',
    'create_client',
    'edit_client',
    'delete_client',
    'view_tasks',
    'create_task',
    'edit_task',
    'delete_task',
    'view_task',
    'view_projects',
    'create_project',
    'edit_project',
    'delete_project',
    'assign_project',
    'view_documents',
    'create_document',
    'edit_document',
    'delete_document',
    'view_document',
    'upload_document',
    'view_invoices',
    'create_invoice',
    'edit_invoice',
    'approve_invoice',
    'view_calendar',
    'create_calendar_event',
    'edit_calendar_event',
    'delete_calendar_event',
    'view_communications',
    'create_communication',
    'delete_communication',
    'edit_communication',
    'view_dashboard',
    'view_reports',
    'create_reports',
    'view_billing',
    'view_financial_reports',
    'manage_trust_balance',
    'close_case'
  ],
  Paralegal: [
    'view_users',
    'view_cases',
    'create_case',
    'edit_case',
    'view_case',
    'view_clients',
    'create_client',
    'edit_client',
    'view_tasks',
    'create_task',
    'edit_task',
    'view_task',
    'view_projects',
    'create_project',
    'edit_project',
    'view_documents',
    'create_document',
    'edit_document',
    'view_document',
    'upload_document',
    'view_calendar',
    'create_calendar_event',
    'edit_calendar_event',
    'view_communications',
    'create_communication',
    'view_dashboard'
  ],
  Staff: [
    'view_users',
    'view_cases',
    'view_case',
    'view_clients',
    'view_tasks',
    'view_task',
    'view_projects',
    'view_documents',
    'view_document',
    'view_calendar',
    'view_communications',
    'view_dashboard'
  ]
};

/**
 * Check if a user has a specific permission based on their roles
 * @param {Object} user - User object with roles array
 * @param {string} permission - Permission name to check
 * @returns {boolean} - True if user has the permission
 */
export const hasPermission = (user, permission) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }

  // Super Admin has all permissions
  if (user.roles.includes('Super Admin')) {
    return true;
  }

  // Check if any of the user's roles has this permission
  return user.roles.some(roleName => {
    const rolePermissions = ROLE_PERMISSIONS[roleName];
    return rolePermissions && rolePermissions.includes(permission);
  });
};

/**
 * Check if a user has a specific role
 * @param {Object} user - User object with roles array
 * @param {string} role - Role name to check
 * @returns {boolean} - True if user has the role
 */
export const hasRole = (user, role) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return user.roles.includes(role);
};

/**
 * Check if a user has any of the specified roles
 * @param {Object} user - User object with roles array
 * @param {Array<string>} roles - Array of role names to check
 * @returns {boolean} - True if user has any of the roles
 */
export const hasAnyRole = (user, roles) => {
  if (!user || !user.roles || !Array.isArray(user.roles)) {
    return false;
  }
  return roles.some(role => user.roles.includes(role));
};

/**
 * Check if a user has all of the specified permissions
 * @param {Object} user - User object with roles array
 * @param {Array<string>} permissions - Array of permission names to check
 * @returns {boolean} - True if user has all permissions
 */
export const hasAllPermissions = (user, permissions) => {
  return permissions.every(permission => hasPermission(user, permission));
};

/**
 * Check if a user has any of the specified permissions
 * @param {Object} user - User object with roles array
 * @param {Array<string>} permissions - Array of permission names to check
 * @returns {boolean} - True if user has any of the permissions
 */
export const hasAnyPermission = (user, permissions) => {
  return permissions.some(permission => hasPermission(user, permission));
};
